import {
  Alert,
  AlertDialog,
  Button,
  DatePicker,
  FormField,
  Input,
  Modal,
  NumberInput,
  ScrollArea,
  Textarea,
} from '@ntconsult/aluma-react';
import { type Ref, useId, useImperativeHandle, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type AllocationFormViewModel, useAllocationForm } from '../hooks/useAllocationForm';
import { useProfessionals } from '../hooks/useProfessionals';
import type { ProjectOption } from '../types/Project';
import { i18nKeyForError } from '../utils/errors';
import { contractLabel, labelWithIso, professionalLabel } from '../utils/labels';
import {
  formatMoney,
  previewAllocatedCost,
  previewGrossRevenue,
  previewNetRevenue,
} from '../utils/money';
import { type EditAllocationTarget, NOTES_MAX_LENGTH } from './AllocationForm.schema';
import { BooleanField } from './BooleanField';
import { SalesProfileSelect } from './SalesProfileSelect';
import { SearchCombobox } from './SearchCombobox';
import { TagsCombobox } from './TagsCombobox';

export type { EditAllocationTarget };

type AllocationModalProps = {
  open: boolean;
  onClose: () => void;
  /** `null` = nova alocação. */
  target: EditAllocationTarget | null;
  today: string;
  readOnly?: boolean;
};

// Literais pro Tailwind JIT achar as classes.
function tagsColSpan(hasId: boolean, bills: boolean): string {
  if (hasId && bills) return 'md:col-span-6';
  if (hasId || bills) return 'md:col-span-9';
  return 'md:col-span-12';
}

// `min-h-6` na label mantém a altura constante com ou sem o ícone ⓘ do hint
// em tooltip (IconButton xs = h-6); sem isso o controle caía ~4px.
const FORM_GRID_CLASS =
  "grid grid-cols-1 gap-4 md:grid-cols-12 [&_label]:min-h-6 [&_label]:after:content-[':']";

type FormBodyHandle = { isDirty: boolean };

type FormBodyProps = Omit<AllocationModalProps, 'open'> & {
  onCancel: () => void;
  ref: Ref<FormBodyHandle>;
};

function FormBody({ target, today, onClose, onCancel, ref, readOnly = false }: FormBodyProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const vm = useAllocationForm({ target, today, readOnly, onSaveSuccess: onClose });
  useImperativeHandle(ref, () => ({ isDirty: vm.isDirty }), [vm.isDirty]);
  return (
    <form onSubmit={vm.onSubmit} className="contents" noValidate>
      <Modal.Body className="flex min-h-0 flex-col overflow-hidden px-0 py-0">
        <ScrollArea className="min-h-0 flex-1 px-6 py-3">
          <div className="flex flex-col gap-4 pr-2">
            <AlertsStrip vm={vm} />
            <div className={FORM_GRID_CLASS}>
              <HeaderFields vm={vm} readOnly={readOnly} />
              <ParticipantFields vm={vm} readOnly={readOnly} />
              <ScopeFields vm={vm} readOnly={readOnly} />
              <CostFields vm={vm} readOnly={readOnly} />
              {vm.isPool && <PoolNoticeFields vm={vm} readOnly={readOnly} />}
              {vm.billsProject && <BillingFields vm={vm} readOnly={readOnly} />}
              <CostShareFields vm={vm} readOnly={readOnly} />
              <DateFields vm={vm} readOnly={readOnly} />
              <NotesField vm={vm} readOnly={readOnly} />
            </div>
          </div>
        </ScrollArea>
      </Modal.Body>
      <Modal.Footer className="border-t border-border">
        {readOnly ? (
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common:close')}
          </Button>
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={onCancel}>
              {t('common:cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={!vm.canSave} loading={vm.isSaving}>
              {target ? t('common:save') : t('alocacao:form.alocar')}
            </Button>
          </>
        )}
      </Modal.Footer>
    </form>
  );
}

function AlertsStrip({ vm }: { vm: AllocationFormViewModel }) {
  const { t } = useTranslation('alocacao');
  const { blockers, warnings } = vm;
  if (blockers.length > 0) {
    return (
      <Alert variant="danger" title={t('form.bloqueioTitulo')}>
        <ul className="list-disc pl-4">
          {blockers.map((b) => (
            <li key={b.code}>{t(i18nKeyForError(b.code) as never)}</li>
          ))}
        </ul>
      </Alert>
    );
  }
  if (warnings.length > 0) {
    return (
      <Alert variant="warning" title={t('form.avisoTitulo')}>
        <ul className="list-disc pl-4">
          {warnings.map((w) => (
            <li key={w.code}>
              {t(i18nKeyForError(w.code) as never, {
                total: (w.data as { maxTotalPercentage?: number } | undefined)?.maxTotalPercentage,
              })}
            </li>
          ))}
        </ul>
      </Alert>
    );
  }
  return null;
}

function HeaderFields({ vm, readOnly }: { vm: AllocationFormViewModel; readOnly: boolean }) {
  const { t } = useTranslation('alocacao');
  const hasId = !!vm.target;
  return (
    <>
      {vm.target && (
        <FormField label={t('form.id')} className="md:col-span-3">
          <Input disabled value={String(vm.target.allocation.id)} />
        </FormField>
      )}
      <FormField
        id="alocacao-tags"
        label={t('form.tags')}
        className={tagsColSpan(hasId, vm.billsProject)}
      >
        <Controller
          control={vm.control}
          name="tagIds"
          render={({ field }) => (
            <TagsCombobox
              id="alocacao-tags"
              label={t('form.tags')}
              value={vm.selectedTags}
              onChange={(next) => {
                vm.setSelectedTags(next);
                field.onChange(next.map((tag) => tag.id));
              }}
              disabled={readOnly}
              placeholder={t('form.tagsPlaceholder')}
              searchPlaceholder={t('form.tagsBuscarPlaceholder')}
              emptyLabel={t('form.tagsSemResultado')}
              removeChipLabel={(name) => t('form.tagsRemover', { nome: name })}
            />
          )}
        />
      </FormField>
      {vm.billsProject && (
        <Controller
          control={vm.control}
          name="billable"
          render={({ field }) => (
            <BooleanField
              id="alocacao-billable"
              label={t('form.faturando')}
              value={field.value}
              onChange={field.onChange}
              disabled={readOnly}
              className="md:col-span-3"
            />
          )}
        />
      )}
    </>
  );
}

function ParticipantFields({ vm, readOnly }: { vm: AllocationFormViewModel; readOnly: boolean }) {
  const { t } = useTranslation('alocacao');
  return (
    <>
      <FormField
        id="alocacao-professional"
        label={t('form.profissional')}
        error={vm.professionalError}
        required
        className="md:col-span-6"
      >
        <Controller
          control={vm.control}
          name="professional"
          render={({ field }) => (
            <SearchCombobox
              id="alocacao-professional"
              placeholder={t('form.profissionalPlaceholder')}
              value={field.value ?? null}
              onChange={(option, item) => {
                field.onChange(option);
                if (item) void vm.applyProfessionalDefaults(item.id);
              }}
              useSearch={useProfessionals}
              getLabel={professionalLabel}
              invalid={!!vm.professionalError}
              disabled={readOnly}
            />
          )}
        />
      </FormField>
      <FormField
        id="alocacao-contract"
        label={t('form.contrato')}
        error={vm.contractError}
        required
        className="md:col-span-6"
      >
        <Controller
          control={vm.control}
          name="contract"
          render={({ field }) => (
            <SearchCombobox
              id="alocacao-contract"
              placeholder={t('form.contratoPlaceholder')}
              value={field.value ?? null}
              onChange={(option) => {
                const previous = vm.getValues('contract');
                field.onChange(option);
                vm.applyContract(option, previous);
              }}
              useSearch={vm.useAllocatableContracts}
              getLabel={contractLabel}
              invalid={!!vm.contractError}
              disabled={readOnly}
            />
          )}
        />
      </FormField>
    </>
  );
}

function ScopeFields({ vm, readOnly }: { vm: AllocationFormViewModel; readOnly: boolean }) {
  const { t } = useTranslation('alocacao');
  return (
    <>
      <FormField
        id="alocacao-project"
        label={t('form.projeto')}
        hint={
          !vm.selectedContractId
            ? t('form.projetoSemContrato')
            : !vm.billsProject
              ? t('form.projetoSemFaturamento')
              : undefined
        }
        hintMode={vm.selectedContractId ? 'tooltip' : 'inline'}
        error={vm.projectError}
        required
        className={vm.billsProject ? 'md:col-span-6' : 'md:col-span-12'}
      >
        <Controller
          control={vm.control}
          name="project"
          render={({ field }) => (
            <SearchCombobox
              id="alocacao-project"
              placeholder={t('form.projetoPlaceholder')}
              value={field.value ? { id: field.value.id, name: field.value.name } : null}
              onChange={(_option, item) => {
                field.onChange(item);
                if (item) vm.applyProjectRules(item);
              }}
              useSearch={vm.useProjectsOfContract}
              getLabel={(p: ProjectOption) => `${p.axisCode} - ${p.name}`}
              invalid={!!vm.projectError}
              disabled={readOnly || !vm.selectedContractId}
            />
          )}
        />
      </FormField>
      {vm.billsProject && (
        <FormField
          id="alocacao-sales-profile"
          label={t('form.perfilVenda')}
          hint={vm.selectedContractId ? undefined : t('form.perfilVendaSemContrato')}
          error={vm.serverErrorFor('salesProfileId')}
          className="md:col-span-6"
        >
          <Controller
            control={vm.control}
            name="salesProfileId"
            render={({ field }) => (
              <SalesProfileSelect
                id="alocacao-sales-profile"
                profiles={vm.salesProfiles}
                value={field.value ?? null}
                currentLabel={
                  vm.allocation?.salesProfile?.id === field.value
                    ? vm.allocation?.salesProfile?.name
                    : undefined
                }
                onChange={vm.applySalesProfile}
                disabled={readOnly || !vm.selectedContractId}
                invalid={!!vm.serverErrorFor('salesProfileId')}
              />
            )}
          />
        </FormField>
      )}
    </>
  );
}

function CostFields({ vm, readOnly }: { vm: AllocationFormViewModel; readOnly: boolean }) {
  const { t, i18n } = useTranslation('alocacao');
  return (
    <>
      <FormField
        id="alocacao-monthly-cost"
        label={labelWithIso(t('form.custoMes'), vm.costIso)}
        hint={t('form.custoMesHint')}
        hintMode="tooltip"
        error={vm.valueError('monthlyCost')}
        className="md:col-span-4"
      >
        <Controller
          control={vm.control}
          name="monthlyCost"
          render={({ field }) => (
            <NumberInput
              id="alocacao-monthly-cost"
              value={field.value}
              onChange={field.onChange}
              min={0}
              precision={2}
              showControls={false}
              {...vm.decimalFormat}
              disabled={readOnly}
            />
          )}
        />
      </FormField>
      <FormField
        id="alocacao-percentage"
        label={t('form.percAlocado')}
        error={
          vm.serverErrorFor('allocatedPercentage') ??
          (vm.errors.allocatedPercentage ? t('erros.invalidPercentage') : undefined)
        }
        required
        className="md:col-span-4"
      >
        <Controller
          control={vm.control}
          name="allocatedPercentage"
          render={({ field }) => (
            <NumberInput
              id="alocacao-percentage"
              value={field.value}
              onChange={field.onChange}
              min={1}
              max={100}
              disabled={readOnly}
            />
          )}
        />
      </FormField>
      <FormField label={labelWithIso(t('form.custoAlocado'), vm.costIso)} className="md:col-span-4">
        <Input
          disabled
          value={formatMoney(
            previewAllocatedCost(vm.monthlyCost, vm.allocatedPercentage),
            vm.costIso,
            i18n.language,
          )}
        />
      </FormField>
    </>
  );
}

function PoolNoticeFields({ vm, readOnly }: { vm: AllocationFormViewModel; readOnly: boolean }) {
  const { t } = useTranslation(['alocacao', 'common']);
  return (
    <>
      <Controller
        control={vm.control}
        name="notified"
        render={({ field }) => (
          <BooleanField
            id="alocacao-notified"
            label={t('alocacao:form.avisado')}
            value={field.value}
            onChange={(checked) => {
              field.onChange(checked);
              vm.applyNotice();
            }}
            disabled={readOnly}
            className="md:col-span-4"
          />
        )}
      />
      <FormField
        id="alocacao-notice-date"
        label={t('alocacao:form.avisadoEm')}
        error={
          vm.serverErrorFor('noticeDate') ??
          (vm.errors.noticeDate ? t('common:errors.required') : undefined)
        }
        required={vm.notified}
        className="md:col-span-4"
      >
        <Controller
          control={vm.control}
          name="noticeDate"
          render={({ field }) => (
            <DatePicker
              id="alocacao-notice-date"
              value={field.value}
              onChange={(date) => {
                field.onChange(date ?? null);
                vm.applyNotice();
              }}
              clearable
              disabled={readOnly || !vm.notified}
            />
          )}
        />
      </FormField>
      <FormField
        id="alocacao-notice-days"
        label={t('alocacao:form.numeroDias')}
        error={vm.serverErrorFor('noticeDays')}
        className="md:col-span-4"
      >
        <Controller
          control={vm.control}
          name="noticeDays"
          render={({ field }) => (
            <NumberInput
              id="alocacao-notice-days"
              value={field.value}
              onChange={(days) => {
                field.onChange(days);
                vm.applyNotice();
              }}
              min={0}
              disabled={readOnly || !vm.notified}
            />
          )}
        />
      </FormField>
    </>
  );
}

function BillingFields({ vm, readOnly }: { vm: AllocationFormViewModel; readOnly: boolean }) {
  const { t, i18n } = useTranslation('alocacao');
  const grossRevenue = previewGrossRevenue(vm.hourlyRate, vm.soldHours);
  return (
    <>
      <FormField
        id="alocacao-hourly-rate"
        label={labelWithIso(t('form.precoHora'), vm.billingIso)}
        error={vm.valueError('hourlyRate')}
        className="md:col-span-3"
      >
        <Controller
          control={vm.control}
          name="hourlyRate"
          render={({ field }) => (
            <NumberInput
              id="alocacao-hourly-rate"
              value={field.value}
              onChange={field.onChange}
              min={0}
              precision={2}
              showControls={false}
              {...vm.decimalFormat}
              disabled={readOnly}
            />
          )}
        />
      </FormField>
      <FormField
        id="alocacao-sold-hours"
        label={t('form.horasVendidas')}
        error={vm.valueError('soldHours')}
        className="md:col-span-3"
      >
        <Controller
          control={vm.control}
          name="soldHours"
          render={({ field }) => (
            <NumberInput
              id="alocacao-sold-hours"
              value={field.value}
              onChange={field.onChange}
              min={0}
              precision={2}
              showControls={false}
              {...vm.decimalFormat}
              disabled={readOnly}
            />
          )}
        />
      </FormField>
      <FormField
        label={labelWithIso(t('form.fatBrutoMes'), vm.billingIso)}
        className="md:col-span-3"
      >
        <Input disabled value={formatMoney(grossRevenue, vm.billingIso, i18n.language)} />
      </FormField>
      <FormField
        label={labelWithIso(t('form.fatLiquidoMes'), vm.billingIso)}
        className="md:col-span-3"
      >
        <Input
          disabled
          value={formatMoney(
            previewNetRevenue(grossRevenue, vm.selectedProfile?.taxRate),
            vm.billingIso,
            i18n.language,
          )}
        />
      </FormField>
    </>
  );
}

function CostShareFields({ vm, readOnly }: { vm: AllocationFormViewModel; readOnly: boolean }) {
  const { t } = useTranslation('alocacao');
  return (
    <>
      <FormField
        id="alocacao-cost-share-br"
        label={t('form.rateioCustoBr')}
        error={vm.serverErrorFor('costShareBr')}
        required
        className="md:col-span-6"
      >
        <Controller
          control={vm.control}
          name="costShareBr"
          render={({ field }) => (
            <NumberInput
              id="alocacao-cost-share-br"
              value={field.value}
              onChange={field.onChange}
              min={0}
              max={100}
              disabled={readOnly}
            />
          )}
        />
      </FormField>
      <FormField
        id="alocacao-cost-share-us"
        label={t('form.rateioCustoUs')}
        error={
          vm.errors.costShareUs?.message
            ? t('form.rateioSomaErro')
            : vm.serverErrorFor('costShareUs')
        }
        required
        className="md:col-span-6"
      >
        <Controller
          control={vm.control}
          name="costShareUs"
          render={({ field }) => (
            <NumberInput
              id="alocacao-cost-share-us"
              value={field.value}
              onChange={field.onChange}
              min={0}
              max={100}
              disabled={readOnly}
            />
          )}
        />
      </FormField>
    </>
  );
}

function DateFields({ vm, readOnly }: { vm: AllocationFormViewModel; readOnly: boolean }) {
  const { t } = useTranslation(['alocacao', 'common']);
  return (
    <>
      <FormField
        id="alocacao-start-date"
        label={t('alocacao:form.dtInicio')}
        error={
          vm.serverErrorFor('startDate') ??
          (vm.errors.startDate ? t('common:errors.required') : undefined)
        }
        required
        className="md:col-span-4"
      >
        <Controller
          control={vm.control}
          name="startDate"
          render={({ field }) => (
            <DatePicker
              id="alocacao-start-date"
              value={field.value}
              onChange={(d) => field.onChange(d as Date)}
              disabled={readOnly}
            />
          )}
        />
      </FormField>
      <FormField
        id="alocacao-end-date"
        label={t('alocacao:form.dtFim')}
        hint={
          vm.isPool && vm.notified ? t('alocacao:form.fimPeloAviso') : t('alocacao:form.dtFimHint')
        }
        hintMode="tooltip"
        error={
          vm.serverErrorFor('endDate') ??
          (vm.errors.endDate?.message ? t('alocacao:erros.endDateBeforeStartDate') : undefined)
        }
        className="md:col-span-4"
      >
        <Controller
          control={vm.control}
          name="endDate"
          render={({ field }) => (
            <DatePicker
              id="alocacao-end-date"
              value={field.value}
              onChange={field.onChange}
              clearable
              disabled={readOnly}
            />
          )}
        />
      </FormField>
      <Controller
        control={vm.control}
        name="fimConfirmado"
        render={({ field }) => (
          <BooleanField
            id="alocacao-fim-confirmado"
            label={t('alocacao:form.fimConfirmado')}
            value={field.value}
            onChange={field.onChange}
            disabled={readOnly}
            className="md:col-span-4"
          />
        )}
      />
    </>
  );
}

function NotesField({ vm, readOnly }: { vm: AllocationFormViewModel; readOnly: boolean }) {
  const { t } = useTranslation('alocacao');
  return (
    <FormField
      label={t('form.anotacoes')}
      error={vm.errors.notes ? t('form.anotacoesLimite', { max: NOTES_MAX_LENGTH }) : undefined}
      className="md:col-span-12"
    >
      <Textarea
        {...vm.register('notes')}
        rows={3}
        maxLength={NOTES_MAX_LENGTH}
        disabled={readOnly}
      />
    </FormField>
  );
}

export function AllocationModal({
  open,
  onClose,
  target,
  today,
  readOnly = false,
}: AllocationModalProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const titleId = useId();
  const formRef = useRef<FormBodyHandle>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  function close() {
    setConfirmingDiscard(false);
    onClose();
  }

  function requestClose() {
    if (formRef.current?.isDirty && !readOnly) setConfirmingDiscard(true);
    else close();
  }

  const title = readOnly
    ? t('alocacao:form.visualizarTitulo')
    : target
      ? t('alocacao:form.editarTitulo')
      : t('alocacao:form.novaTitulo');

  return (
    <Modal
      open={open}
      onClose={requestClose}
      closeOnBackdrop={false}
      size="4xl"
      ariaLabelledBy={titleId}
    >
      <Modal.Header
        titleId={titleId}
        title={title}
        onClose={requestClose}
        dismissLabel={t('common:close')}
        className="border-b border-border pb-4"
      />
      {open && (
        <FormBody
          key={target?.allocation.id ?? 'nova'}
          target={target}
          today={today}
          onClose={close}
          onCancel={requestClose}
          ref={formRef}
          readOnly={readOnly}
        />
      )}
      <AlertDialog
        open={confirmingDiscard}
        onOpenChange={(next) => !next && setConfirmingDiscard(false)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>{t('alocacao:form.descartarTitulo')}</AlertDialog.Title>
          <AlertDialog.Description>{t('alocacao:form.descartarDescricao')}</AlertDialog.Description>
          <AlertDialog.Footer>
            <AlertDialog.Cancel type="button">
              {t('alocacao:form.continuarEditando')}
            </AlertDialog.Cancel>
            <AlertDialog.Action type="button" variant="danger" onClick={close}>
              {t('alocacao:form.descartar')}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </Modal>
  );
}
